import { BdayLogo } from "@/components/bday-logo";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { DeletionBanner } from "@/components/deletion-banner";
import { RouteProgress } from "@/components/route-progress";
import { SignupDetailsGate } from "@/site/components/auth/signup-details-gate";
import { AccountClaimFinisher } from "@/site/components/auth/account-claim";
import { isEmbeddedInApp } from "@/lib/native-bridge";
import { MobileInstallBanner } from "@/site/components/mobile-install-banner";
import { SiteLayout } from "@/site/components/site-layout";
import { SiteHeader } from "@/site/components/site-header";
import { SimpleFooter } from "@/site/components/site-footer";
import { ThemeProvider } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { usePostAuthFunnel } from "@/hooks/use-post-auth-funnel";
import { toastManager } from "@/hooks/use-toast";
import { useVersionCheck } from "@/hooks/use-version-check";
import { WebSocketProvider } from "@/hooks/use-websocket";
import AuthPage, { type AuthStage } from "@/site/pages/auth";
import { sanitizeNext } from "@/site/components/auth/auth-utils";
import NotFound from "@/site/pages/not-found";
import { Toast } from "@base-ui/react/toast";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  Navigate,
  Outlet,
  useLocation,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { AUTH_ME_QUERY_KEY, queryClient } from "../lib/queryClient";
import { applyPageMeta } from "@/lib/page-meta";
import { HAD_SESSION_AT_BOOT } from "@/lib/session-hint";

const AdminLayout = lazy(() =>
  import("@/admin/components/admin-layout").then((m) => ({ default: m.AdminLayout })),
);
const EmailVerifyPage = lazy(() => import("@/site/pages/email-verify"));

/**
 * The three legacy auth pages. `/auth` and `/forgot-password` are the
 * interim-rewrite names those replaced; both stay as redirects so old links,
 * bookmarks and already-sent emails keep working.
 */
const AUTH_STAGE_BY_PATH: Record<string, AuthStage> = {
  "/login": "login",
  "/register": "register",
  "/forgot": "forgot",
};

function LoadingScreen() {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="flex items-center justify-center h-dvh bg-background"
    >
      {/* Black-on-transparent wordmark; invert under `.dark` (admin pre-paint in index.html). */}
      <BdayLogo size={56} className="dark:invert" aria-hidden />
    </div>
  );
}

/**
 * The email-verification intercept's shell — the one screen a new member meets
 * between registering and the site. It wears the same chrome as /login, /register
 * and /forgot (`AuthPage`): `bg-background`, the simple tan header bar, the
 * 600px centered column, the simple footer — so the step after the register page
 * looks like the register page, not a different shell.
 *
 * History: a hardcoded pink→orange gradient under a black overlay (Bebo leftover),
 * then a bare `bg-grey-faint` card (BDAY-181, Erin). The header's logo still links
 * home, but the intercept holds at every gated path, so that's a no-op here.
 */
function OnboardingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <DeletionBanner />
      <SiteHeader simpleChrome />
      <main id="main-content" className="min-h-[80vh]">
        <div className="mx-auto max-w-[600px] px-5 py-10">
          <div className="flex w-full flex-col items-center gap-4">{children}</div>
        </div>
      </main>
      <SimpleFooter />
    </div>
  );
}

/**
 * Redirect to a raw path that may carry its own query string — what `?next=`
 * holds ("/reminders?tab=upcoming"). `<Navigate to=…>` only accepts a route id,
 * so this goes through the imperative API, which takes a built `href`.
 */
function RedirectToHref({ href }: { href: string }) {
  const router = useRouter();
  useEffect(() => {
    void router.navigate({ href, replace: true });
  }, [href, router]);
  return <LoadingScreen />;
}

/**
 * `enabled={false}` is rendered (not omitted) by the signed-in branches so every
 * branch below has the SAME element shape — `<Suspense><GlobalScrollToTop/><X/></Suspense>`.
 * Signing in (authwall over the composer, 2026-10-23) used to swap the signed-out
 * `<Suspense>[scroll, SiteLayout]</Suspense>` for a bare `<SiteLayout/>`, which React
 * treats as a different tree: SiteLayout, its Outlet and the composer all remounted.
 */
function GlobalScrollToTop({ enabled = true }: { enabled?: boolean }) {
  const location = useLocation();
  useEffect(() => {
    if (enabled) window.scrollTo(0, 0);
  }, [location.pathname, enabled]);
  return null;
}

/**
 * Email-link soft login (legacy t/e parity, 2026-08-03): any route can land
 * with `?slt=<signed token>` from a reminder email. Exchange it for the 24h
 * soft session, strip the param from the URL, and refresh auth state — the
 * member goes email → composer signed in, without a password round-trip.
 */
/**
 * True while an `?slt=` exchange is in flight.
 *
 * Module-level rather than context so `AppLayout` can read it on its very first
 * render, before any provider has mounted. Without it the `/me` 401 (fast) beats
 * the exchange (a round trip), `AppLayout` takes the logged-out branch, and a
 * valid one-click email link to a gated page — `/reminders`, `/account` — flashes
 * the login screen before correcting itself.
 */
let softLoginPending = new URLSearchParams(window.location.search).has("slt");
const softLoginWaiters = new Set<() => void>();
/**
 * True from the exchange request until it settles. The handler's effect re-runs on
 * every `location.search` change, and a redirect that lands mid-exchange (the billing
 * emails' `/account/billing` → `/payment`, whose `validateSearch` drops `slt`) re-runs
 * it with no token. Releasing the gate there let the boot `/me` 401 bounce the member
 * to /login before the soft session existed.
 */
let softLoginInFlight = false;
function finishSoftLogin() {
  softLoginPending = false;
  for (const notify of softLoginWaiters) notify();
  softLoginWaiters.clear();
}

function useSoftLoginPending(): boolean {
  const [pending, setPending] = useState(softLoginPending);
  useEffect(() => {
    if (!softLoginPending) {
      setPending(false);
      return;
    }
    const notify = () => setPending(false);
    softLoginWaiters.add(notify);
    return () => {
      softLoginWaiters.delete(notify);
    };
  }, []);
  return pending;
}

function SoftLoginHandler() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("slt");
    if (!token) {
      if (!softLoginInFlight) finishSoftLogin();
      return;
    }
    params.delete("slt");
    const clean =
      window.location.pathname +
      (params.size ? `?${params.toString()}` : "") +
      window.location.hash;
    window.history.replaceState(window.history.state, "", clean);
    softLoginInFlight = true;
    void fetch("/api/public/auth/soft-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (res.ok) {
          // The boot `/me` usually went out BEFORE this exchange set the cookie, and is
          // still in flight. invalidateQueries alone dedupes onto that request (a first
          // fetch has no data to cancel for), so its 401 stuck and the page showed a
          // soft-signed-in member as logged out — every cake/Get Started hit the authwall
          // (Nick 10-13). Cancel the stale request first, then fetch fresh.
          await queryClient.cancelQueries({ queryKey: AUTH_ME_QUERY_KEY });
          await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
        }
      })
      .catch(() => {
        // Invalid/expired token → the page just renders logged-out.
      })
      // Release the gate either way: a failed exchange must not wedge the app
      // on the loading screen.
      .finally(() => {
        softLoginInFlight = false;
        finishSoftLogin();
      });
  }, [location.search]);
  return null;
}

/**
 * Public, crawlable pages that may paint BEFORE `/me` answers (for a browser with no
 * session hint — see session-hint.ts). Deliberately a short allowlist: pages whose
 * auth use is cosmetic, where "logged-out until told otherwise" is the right default.
 * Anything with auth-driven side effects (composer anon drafts, card read/sign,
 * share accept, reminders add, support) keeps waiting for `/me`.
 */
function isEarlyPaintPath(pathname: string): boolean {
  if (EARLY_PAINT_PATHS.has(pathname)) return true;
  // `/cards/browse` is the soft-session reminder flow, not the public browser.
  if (pathname.startsWith("/cards/")) return pathname !== "/cards/browse";
  return (
    pathname.startsWith("/card/view/") ||
    pathname.startsWith("/name/") ||
    pathname.startsWith("/faq/") ||
    pathname.startsWith("/help/")
  );
}
const EARLY_PAINT_PATHS = new Set([
  "/",
  "/home",
  "/cards",
  "/name",
  "/infographic",
  "/faq",
  "/help",
  "/about",
  "/company",
  "/privacy",
  "/terms",
  "/safety",
]);

function AppLayout() {
  const { user, softUser, isLoading } = useAuth();
  const softLoginPendingNow = useSoftLoginPending();
  const location = useLocation();
  const pathname = location.pathname;

  const authStage = AUTH_STAGE_BY_PATH[pathname];
  const isLegacyAuthAlias = pathname === "/auth" || pathname === "/forgot-password";
  const authSearch = new URLSearchParams(location.searchStr);
  const isEmbedded = isEmbeddedInApp();

  // Chrome-free routes (no site header/footer), for any visitor: the print view
  // (standalone tab), the card composer (full-screen builder takeover), and the
  // recipient card-read page — the old app's `routes/clean` group. The read surface
  // carries its own logo + copyright inside the card wrapper in place of the chrome.
  // Rendered inside the mobile app's card webview (PRD 12 D13): the native screen
  // supplies the header, so drop the site chrome.
  const isChromeFree =
    pathname === "/reminders-print-view" ||
    pathname === "/card/send/create" ||
    pathname.startsWith("/card/read/") ||
    // The public share view is the same chromeless card wrapper (legacy CardShareView).
    pathname.startsWith("/card/share/view/") ||
    // The signer view — also in the old `routes/clean` group (`<LayoutPage clean />`).
    pathname.startsWith("/card/sign/view/") ||
    // The mobile gift-SDK webview host: full-bleed, no chrome, stateless (PRD 10 §9).
    pathname === "/gift/sdk-host" ||
    isEmbedded;

  // Hold the loading screen while an email-link exchange is in flight, so a
  // valid `?slt=` link never flashes the login page on its way in.
  if (isLoading || softLoginPendingNow) {
    // Public pages paint immediately instead of behind a logo screen: waiting on
    // `/me` serialized the whole page (auth round trip → page queries → LCP image).
    // The element shape is chosen to MATCH the branch `/me` will most likely land
    // in, so when it answers React keeps the same tree — no remount, no refetch:
    //  - no session hint (first-time visitor, crawler, signed-out member) → the
    //    logged-out branch's shape below;
    //  - hint set (returning member) → the signed-in branch's shape (the final
    //    `<SiteLayout />`). Auth-shaped bits render their signed-in/neutral form
    //    meanwhile (header nav, the "Picked for you" strip — HAD_SESSION_AT_BOOT).
    //    `/` is excluded: a member there is redirected to /home, so painting the
    //    marketing page first would flash it.
    // Not inside the mobile app's webview: it signs in via an injected token, so
    // guessing the session there is wrong (and it isn't crawled).
    if (!softLoginPendingNow && !isEmbedded && isEarlyPaintPath(pathname)) {
      if (!HAD_SESSION_AT_BOOT) {
        return (
          <Suspense>
            <GlobalScrollToTop />
            <SiteLayout />
          </Suspense>
        );
      }
      if (pathname !== "/")
        return (
          <Suspense>
            <GlobalScrollToTop enabled={false} />
            <SiteLayout />
          </Suspense>
        );
    }
    // /login, /register, /forgot (CWV pass, 2026-11-03): 30 % of search clicks land on
    // /login — signed-out members looking for the form — and it sat behind the logo screen
    // until `/me` said 401. No session hint → paint the form now; the element shape is the
    // `!user` branch's below, so React keeps the tree when `/me` answers. A hinted member
    // keeps the loading screen: they are redirected away from here, so a flashed form
    // would be wrong.
    if (!softLoginPendingNow && !isEmbedded && !HAD_SESSION_AT_BOOT && authStage) {
      return (
        <>
          <GlobalScrollToTop />
          <AuthPage stage={authStage} />
        </>
      );
    }
    return <LoadingScreen />;
  }

  // PRD 13 O19 (2026-09-16): legacy password-RESET links were `/change_password?t=&e=`.
  // Land them on /forgot with an explanation, for every visitor — decided here, not in
  // the route's beforeLoad, because this layout's login bounce runs first.
  if (pathname === "/change_password" && authSearch.has("t") && authSearch.has("e")) {
    return <Navigate to="/forgot" search={{ error: "legacy_reset_link" }} replace />;
  }

  // Terms and privacy pages must render for any visitor, including users mid-onboarding
  // who tap the links from the terms-agreement / oath / verify screens in a new tab.
  if (pathname === "/terms" || pathname === "/privacy") {
    return (
      <Suspense>
        <GlobalScrollToTop />
        <SiteLayout />
      </Suspense>
    );
  }

  if (!user?.is_ai) {
    // "Hold the send, not the flow" (2026-10-06): a member who signed up inside the card
    // composer is NOT pulled out into the verify page — they finish and schedule the card
    // (the server accepts their pending address; the drain holds the card until it
    // verifies) and the success popup (`/home?sent=`) tells them to verify. Everywhere
    // else the verify page stays the gate.
    const inSendFlow =
      pathname === "/card/send/create" || (pathname === "/home" && authSearch.has("sent"));
    // 2026-10-20 review P1-22 (owner decision): a member migrated from legacy is never
    // walled — legacy never gated sign-in on verification, the wall auto-sends a code on
    // every visit, and it has no exit for a suppressed/absent address. New v2 sign-ups
    // keep the gate exactly as before; /verify and the account emails panel still let a
    // migrated member verify when they choose to.
    // The email's verify button lands on /email-link, which must render for the very member
    // this gate would otherwise take over (BDAY-479).
    const needsEmailVerification =
      user &&
      !user.is_email_verified &&
      !user.is_legacy_member &&
      !inSendFlow &&
      pathname !== "/email-link";

    if (needsEmailVerification) {
      return (
        <OnboardingShell>
          <Suspense>
            <EmailVerifyPage />
          </Suspense>
        </OnboardingShell>
      );
    }
  }

  if (!user) {
    const publicPaths = [
      "/",
      "/home",
      "/privacy",
      "/terms",
      "/safety",
      "/faq",
      "/help",
      "/support",
      "/about",
      "/company",
      "/contact", // linked from the public terms/privacy pages
      "/infographic", // SEO landing (PRD 09 D3) — bots got the OG page, humans bounced to /login
      "/name",
      // The greeting-card landing pages behind legacy's Send Own Birthday / Send Own
      // Anniversary mail and our reminder-self mail (2026-09-16). Legacy bounced a
      // visitor without a member to `/`; we just render — it's a greeting.
      "/happy_birthday",
      "/anniversary/gift",
      // The public "add birthdays" page — the logged-out reminders landing (old public
      // /reminders/add, which now redirects here) and the logged-in Add Reminders page in
      // one. Every other /reminders route stays login-gated (/reminders bounces to
      // /login?next= — returning members from reminder emails want the login form).
      "/reminders/birthday/add",
      // The mobile CloudSponge import WebView host (PRD 02 §5.8). Like /gift/sdk-host
      // it renders unauthenticated and authenticates its import POST via the injected
      // __BDAY_APP_TOKEN__ — so a token-timing hiccup can't bounce it to /login.
      "/contacts/import",
    ];
    // The reminder-email browse flow is the one member-only surface a SOFT
    // session may enter (PRD 08 D18): it's where every reminder CTA lands. A
    // fully logged-out visitor still bounces to /login?next= (their soft session
    // expired — after signing in they come straight back). The old
    // `/cards/browse` path fell under `startsWith("/cards/")` and was public by
    // accident; the 09-15 rename to the legacy paths silently closed that door.
    const isSoftFlow =
      !!softUser &&
      (pathname === "/birthday/browse" ||
        pathname === "/anniversary/browse" ||
        pathname === "/holiday/browse" ||
        // D18a: "your card is scheduled" email → status of THAT card only.
        (pathname.startsWith("/mycards/") && authSearch.has("card_send_id")) ||
        // D18b: billing-recovery email → "add a card & pay" only (legacy /payment kept).
        (pathname === "/account/billing" &&
          (authSearch.has("recover") || authSearch.has("resume"))) ||
        pathname === "/payment");
    const isPublic =
      isSoftFlow ||
      publicPaths.includes(pathname) ||
      pathname.startsWith("/faq/") ||
      pathname.startsWith("/help/") ||
      pathname.startsWith("/name/") || // SEO name pages, same as /infographic
      pathname === "/cards" ||
      pathname.startsWith("/cards/") ||
      pathname.startsWith("/card/") ||
      pathname.startsWith("/s/") ||
      pathname.startsWith("/dob/") ||
      pathname.startsWith("/shares/") ||
      // Legacy Birthday-Request magic links (`/bday/<magic>`, `/bd4..bd7/<magic>`) —
      // the token IS the credential and the recipient is signed out by definition.
      pathname.startsWith("/bday/") ||
      /^\/bd[4-7]\//.test(pathname) ||
      pathname.startsWith("/support/ticket/") ||
      pathname.startsWith("/unsubscribe") ||
      // BDAY-479: the verify / reset email button. Signed out is a normal state there (it
      // offers sign-in itself); the token, not a session, is what it starts from.
      pathname === "/email-link" ||
      // The mobile gift-SDK webview host is unauthenticated (it gets everything from
      // the injected __GIFT_INIT__); let it through instead of bouncing to /login.
      pathname === "/gift/sdk-host";

    // Old names → new routes, query string intact.
    if (pathname === "/auth") {
      const nextParam = authSearch.get("next");
      const errorParam = authSearch.get("error");
      return (
        <Navigate
          to="/login"
          search={{
            ...(nextParam ? { next: nextParam } : {}),
            ...(errorParam ? { error: errorParam } : {}),
          }}
          replace
        />
      );
    }
    if (pathname === "/forgot-password") {
      const nextParam = authSearch.get("next");
      return <Navigate to="/forgot" search={nextParam ? { next: nextParam } : {}} replace />;
    }

    if (authStage) {
      return (
        <>
          <GlobalScrollToTop />
          <AuthPage stage={authStage} />
        </>
      );
    }

    // Everything else that isn't public bounces to /login carrying where they were
    // headed, which is how the legacy route guard behaved (`<Navigate to="/login">`
    // + `?next=`). The interim rewrite rendered the auth wall in place instead, so
    // there was nothing to come back to after signing in.
    if (!isPublic) {
      return <Navigate to="/login" search={{ next: pathname + location.searchStr }} replace />;
    }

    if (isChromeFree) {
      return (
        <Suspense>
          <GlobalScrollToTop />
          <Outlet />
        </Suspense>
      );
    }

    return (
      <Suspense>
        <GlobalScrollToTop />
        <SiteLayout />
      </Suspense>
    );
  }

  // Resume a Reminder Share the user opened before signing up: share-accept.tsx stashes
  // the token, sends them to sign up, and we return them here to finish adding the dates.
  // Signed in and still sitting on an auth page — send them on. This is the ONE
  // post-auth redirect in the app: password login, register, OAuth return, a
  // soft-login link and an already-signed-in visitor all arrive here, so `?next=`
  // is honoured for every one of them (legacy `onAuthSuccess`).
  if (authStage || isLegacyAuthAlias) {
    // `href` rather than `to`: `next` is a raw path that may carry its own query
    // string ("/reminders?tab=upcoming"), which `to` does not accept.
    const next = sanitizeNext(authSearch.get("next"));
    return next ? <RedirectToHref href={next} /> : <Navigate to="/" replace />;
  }

  // Resume a Reminder Share the user opened before signing up: share-accept.tsx
  // stashes the token, sends them to sign in, and we return them here to finish
  // adding the dates. Sits BELOW the auth branch (it used to sit above it, and
  // so hijacked an explicit `?next=`), and replaces rather than pushes — a push
  // made Back bounce straight off the page that redirects.
  // Safari private mode / blocked site data make this throw rather than return null.
  let pendingShare: string | null = null;
  try {
    pendingShare = sessionStorage.getItem("bday_pending_share");
  } catch {
    /* no session storage — nothing stashed */
  }
  if (pendingShare && !pathname.startsWith("/shares/")) {
    return <Navigate to="/shares/$token" params={{ token: pendingShare }} replace />;
  }

  // Admin is all-or-nothing. Previously a non-admin simply fell through to
  // `<SiteLayout/>`, whose `<Outlet/>` then rendered the real admin route
  // component inside the customer chrome — every data call 403s, but the shell
  // renders. Reachable by anyone via `?next=/admin/...` off a login bounce.
  if (pathname.startsWith("/admin")) {
    return user.is_admin ? (
      <Suspense fallback={<LoadingScreen />}>
        <AdminLayout />
      </Suspense>
    ) : (
      <Navigate to="/" replace />
    );
  }

  // Same shape as the signed-out branches (see GlobalScrollToTop) so a sign-in in
  // place keeps the page mounted.
  if (isChromeFree) {
    return (
      <Suspense>
        <GlobalScrollToTop enabled={false} />
        <Outlet />
      </Suspense>
    );
  }

  return (
    <Suspense>
      <GlobalScrollToTop enabled={false} />
      <SiteLayout />
    </Suspense>
  );
}

function VersionChecker() {
  useVersionCheck();
  return null;
}

/** Title + description/og tags from `shared/seo.ts` on every navigation (page overrides win). */
function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    applyPageMeta(pathname);
  }, [pathname]);
  return null;
}

/**
 * Finish any Birthday-Request funnel action + reconcile anonymous signatures the
 * moment the viewer becomes authenticated.
 *
 * Mounted here rather than in `SiteLayout` (moved 2026-08-18): social sign-in now
 * runs in a popup, so the member stays exactly where they were instead of being
 * dropped on `/`. That broke the old placement, which never covered the
 * chrome-free routes — `/card/read/*` is precisely where the cake dialog stashes
 * its pending action, and it renders outside `SiteLayout`. The root is mounted on
 * every route, and the hook is idempotent (it clears the pending action and
 * stamps `bday_reconciled:<userId>` before dispatching).
 */
function PostAuthFunnel() {
  usePostAuthFunnel();
  return null;
}

function RootComponent() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toast.Provider toastManager={toastManager} timeout={5000} limit={3}>
            <AuthProvider>
              {/* Runs on EVERY route, before/independent of the auth branches below. */}
              <SoftLoginHandler />
              <PostAuthFunnel />
              {/* Top progress bar while a navigation waits on its route chunk. */}
              <RouteProgress />
              {/* PRD 08 D9 sign-in-as banner; first in flow so it pushes the header down. */}
              <ImpersonationBanner />
              <WebSocketProvider>
                <AppLayout />
              </WebSocketProvider>
              {/* PRD 08 D19: a new social sign-up owes its birthday before anything else. */}
              <SignupDetailsGate />
              {/* PRD 08 D21: finish an "I already have an account" / self-serve merge. */}
              <AccountClaimFinisher />
            </AuthProvider>
            <MobileInstallBanner />
            <VersionChecker />
            <RouteMeta />
            <Toaster />
          </Toast.Provider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

/**
 * Last line of defence for a render throw below the root. No providers here —
 * those live in `RootComponent`, which is what just failed.
 */
function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const detail = error instanceof Error ? error.message : "";
  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background">
      <Card className="mx-4 w-full max-w-md">
        <CardContent className="pt-6">
          <div className="mb-4 flex gap-2">
            <AlertCircle className="h-8 w-8 text-red-500" aria-hidden />
            <h1 className="text-2xl font-bold text-foreground" data-testid="text-error-title">
              Something went wrong
            </h1>
          </div>
          <p className="mt-4 text-base text-muted-foreground" data-testid="text-error-desc">
            That page didn&rsquo;t load. It&rsquo;s usually temporary — try again.
          </p>
          {detail && <p className="mt-2 text-sm text-muted-foreground/80 break-words">{detail}</p>}
          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              onClick={() => {
                reset();
                void router.invalidate();
              }}
              data-testid="button-error-retry"
            >
              Try again
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.assign("/")}
              data-testid="button-error-home"
            >
              Go to homepage
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: NotFound,
});
